import { Check } from 'lucide-react';
import { Checkbox as C, Switch as S, Tabs as T } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/cn';

// ---------- Kbd ----------

export function Kbd({
  children,
  inverted,
  className,
}: {
  children: ReactNode;
  inverted?: boolean;
  className?: string;
}) {
  return (
    <kbd
      className={cn(
        'inline-flex h-4.5 min-w-4.5 items-center justify-center rounded px-1 font-sans text-2xs font-medium',
        inverted ? 'bg-white/20 text-bg' : 'border border-border bg-subtle text-fg-muted',
        className,
      )}
    >
      {children}
    </kbd>
  );
}

// ---------- Badge ----------

type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';
const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-subtle text-fg-muted border-border',
  accent: 'bg-accent-soft text-accent-text border-transparent',
  success: 'bg-success-soft text-success border-transparent',
  warning: 'bg-warning-soft text-warning border-transparent',
  danger: 'bg-danger-soft text-danger border-transparent',
};

export function Badge({
  tone = 'neutral',
  color,
  className,
  children,
  ...props
}: ComponentProps<'span'> & {
  tone?: BadgeTone;
  /** Custom hex color, e.g. a status or tag color. */ color?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-5 max-w-full items-center gap-1 rounded border px-1.5 text-xs font-medium whitespace-nowrap',
        color ? 'border-transparent' : TONES[tone],
        className,
      )}
      style={color ? { backgroundColor: `color-mix(in srgb, ${color} 16%, transparent)`, color } : undefined}
      {...props}
    >
      {children}
    </span>
  );
}

// ---------- Skeleton ----------

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('skeleton h-4', className)} />;
}

/** Stack of skeleton rows for list-like pages. */
export function SkeletonRows({ rows = 6, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)} role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-8" />
      ))}
    </div>
  );
}

// ---------- Empty state ----------

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-16 text-center', className)}>
      {icon ? (
        <div className="mb-4 flex size-12 items-center justify-center rounded-xl bg-accent-soft text-accent-text [&_svg]:size-6">
          {icon}
        </div>
      ) : null}
      <h2 className="text-base font-semibold text-fg">{title}</h2>
      {description ? <p className="mt-1 max-w-sm text-sm text-fg-muted">{description}</p> : null}
      {action ? <div className="mt-5 flex items-center gap-2">{action}</div> : null}
    </div>
  );
}

// ---------- Checkbox / Switch ----------

export function Checkbox({ className, ...props }: ComponentProps<typeof C.Root>) {
  return (
    <C.Root
      className={cn(
        'flex size-4 shrink-0 items-center justify-center rounded border border-border-strong bg-surface',
        'data-[state=checked]:border-accent data-[state=checked]:bg-accent data-[state=checked]:text-accent-fg',
        'data-[state=indeterminate]:border-accent data-[state=indeterminate]:bg-accent',
        className,
      )}
      {...props}
    >
      <C.Indicator>
        <Check className="size-3" strokeWidth={3} />
      </C.Indicator>
    </C.Root>
  );
}

export function Switch({ className, ...props }: ComponentProps<typeof S.Root>) {
  return (
    <S.Root
      className={cn(
        'relative h-5 w-9 shrink-0 rounded-full bg-border-strong transition-colors data-[state=checked]:bg-accent',
        className,
      )}
      {...props}
    >
      <S.Thumb className="block size-4 translate-x-0.5 rounded-full bg-white shadow transition-transform data-[state=checked]:translate-x-[18px]" />
    </S.Root>
  );
}

// ---------- Tabs ----------

export const Tabs = T.Root;
export const TabsContent = T.Content;

export function TabsList({ className, ...props }: ComponentProps<typeof T.List>) {
  return <T.List className={cn('flex items-center gap-1 border-b border-border', className)} {...props} />;
}

export function TabsTrigger({ className, ...props }: ComponentProps<typeof T.Trigger>) {
  return (
    <T.Trigger
      className={cn(
        '-mb-px inline-flex h-9 items-center gap-1.5 border-b-2 border-transparent px-2.5 text-sm font-medium text-fg-muted',
        'hover:text-fg data-[state=active]:border-accent data-[state=active]:text-fg',
        className,
      )}
      {...props}
    />
  );
}

// ---------- Separator ----------

export function Separator({ className, vertical }: { className?: string; vertical?: boolean }) {
  return (
    <div role="separator" className={cn('bg-border', vertical ? 'h-4 w-px' : 'h-px w-full', className)} />
  );
}
